import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {VENDOR_ADMIN} from 'src/hooks/access-resource-constant';
import {useApprovalRequestActions} from 'src/modules/vendor-admin/hooks/useApprovalRequestActions';
import {useRequestsTable} from 'src/modules/vendor-admin/hooks/useRequestsTable';
import {
    selectIsLoadingRequests,
    selectRequests,
    selectRequestsTotalResults,
} from 'src/modules/vendor-admin/service/requests.slice';
import {
    ApprovalRequestSearchCriteria,
    ApprovalRequestSearchCriteriaSortOrder,
} from 'src/openapi/admin/models/ApprovalRequestSearchCriteria';
import {ApprovalRequestSearchItem} from 'src/openapi/admin/models/ApprovalRequestSearchItem';
import {RequestStatus} from 'src/openapi/admin/models/RequestStatus';
import {User} from 'src/openapi/admin/models/User';

import {FlatButton} from '@pb/kit-component-button';
import Group from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {useShown} from '@pb/kit-hooks-use-shown';

import {DenyRequestDialog} from '../components/deny-request-dialog';
import {RequestsLoadingState} from '../components/requests-loading-state';
import {RequestsTable} from '../components/requests-table';
import {ToolsRibbonComponent} from '../components/tools-ribbon-component';
import {RequestTopFilters} from '../filters/request-top-filters';


type Props = {
    criteria: ApprovalRequestSearchCriteria,
    onUpdateCriteria: (criteria: Partial<ApprovalRequestSearchCriteria>) => void,
    refreshData: () => void,
    hasAccessToResource: (resource: string) => boolean,
    currentUser: User | null,
};

export const PendingRequestsTabContent = (props: Props) => {
    const {criteria, onUpdateCriteria, refreshData, hasAccessToResource, currentUser} = props;
    const hasActiveFilter = !!(criteria.email || criteria.firstName || criteria.lastName || criteria.vendorId);
    const requests = useSelector(selectRequests);

    const isLoadingRequests = useSelector(selectIsLoadingRequests);
    const requestsTotalCount = useSelector(selectRequestsTotalResults);
    const {
        selectedIds,
        clearAll,
        selectPage,
        setSelectedIds,
    } = useRequestsTable(requests);
    const pageSelectedCount = requests.reduce(
        (count, request) => count + (selectedIds.includes(request.requestId) ? 1 : 0),
        0,
    );
    const [requestToDeny, setRequestToDeny] = useState<ApprovalRequestSearchItem | null>(null);
    const {approve, decline} = useApprovalRequestActions();
    const {onClose, onToggle, shown} = useShown();
    const canApprove = useMemo(() => hasAccessToResource(VENDOR_ADMIN.APPROVE_REQUESTS), [hasAccessToResource]);
    const canDecline = useMemo(() => hasAccessToResource(VENDOR_ADMIN.DECLINE_REQUESTS), [hasAccessToResource]);

    const handleDenyClick = useCallback((request: ApprovalRequestSearchItem) => {
        setRequestToDeny(request);
        onToggle();
    }, [onToggle]);

    const cleanSelection = useCallback((requestId: string) => {
        if (!selectedIds.length) {
            return;
        }

        const filteredIds = selectedIds.filter(id => id !== requestId);
        setSelectedIds(filteredIds);
    }, [setSelectedIds, selectedIds]);

    const handleDenyConfirm = useCallback(async (requestId: string, reason: string) => {
        const ok = await decline(requestId, {reason});
        setRequestToDeny(null);
        if (ok) {
            refreshData();
            cleanSelection(requestId);
        }
    }, [decline, refreshData, cleanSelection]);

    const handleApprove = useCallback(async (requestId: string) => {
        const ok = await approve(requestId);
        if (ok) {
            refreshData();
            cleanSelection(requestId);
        }
    }, [approve, refreshData, cleanSelection]);

    const handleDenyClose = useCallback(() => {
        setRequestToDeny(null);
        onClose();
    }, [onClose]);

    useEffect(() => {
        if (isLoadingRequests) {
            return;
        }
        const maxPage = Math.max(1, Math.ceil((requestsTotalCount || 0) / criteria.pageSize));
        if (criteria.page > maxPage) {
            onUpdateCriteria({page: maxPage, pageSize: criteria.pageSize});
        }
    }, [isLoadingRequests, requestsTotalCount, criteria.page, criteria.pageSize, onUpdateCriteria]);

    const renderActionColumn = useCallback((request: ApprovalRequestSearchItem) => (

        <Group direction="h" justify="space-around" gap="xs">
            {
                canApprove ? (
                    <FlatButton
                        style={{padding: 4}}
                        startIcon={<Icon type={Icons.CHECK} size="Size_16"/>}
                        onClick={() => handleApprove(request.requestId)}>
                    </FlatButton>
                ) : null
            }
            {
                canDecline ? (
                    <FlatButton
                        style={{padding: 4}}
                        startIcon={<Icon type={Icons.CROSS} size="Size_12"/>}
                        onClick={() => handleDenyClick(request)}>
                    </FlatButton>
                ) : null
            }
        </Group>
    ), [handleApprove, handleDenyClick, canApprove, canDecline]);

    useEffect(() => {
        if (!currentUser) {
            return;
        }
        const newCriteria: Partial<ApprovalRequestSearchCriteria> = {
            requestStatuses: [RequestStatus.CREATED],
            page: 1,
            pageSize: 25,
            sortOrder: ApprovalRequestSearchCriteriaSortOrder.DESC,
        };
        if (currentUser?.vendor?.id) {
            newCriteria.vendorId = currentUser.vendor.id;
        }
        onUpdateCriteria(newCriteria);
    }, [onUpdateCriteria, currentUser]);

    return (
        <>
            <RequestTopFilters
                criteria={criteria}
                onChange={onUpdateCriteria}
                currentUser={currentUser}
            />
            <ToolsRibbonComponent
                pageIds={requests.map(r => r.requestId)}
                selectedIds={selectedIds}
                pageSelectedCount={pageSelectedCount}
                onSelectPage={selectPage}
                onClear={clearAll}
            />
            {isLoadingRequests && !requests.length ? (
                <RequestsLoadingState/>
            ) : (
                <>
                    <RequestsTable
                        requests={requests}
                        criteria={criteria}
                        onUpdateCriteria={onUpdateCriteria}
                        enableStatusFilter={false}
                        hasActiveFilter={hasActiveFilter}
                        noResultsMessage="There are currently no pending requests"
                        actionColumn={(canApprove || canDecline) ? renderActionColumn : undefined}
                        totalCount={requestsTotalCount}
                    />
                </>
            )}

            {requestToDeny && requests.find(r => r.requestId === requestToDeny.requestId) ? (
                <DenyRequestDialog
                    shown={shown}
                    onClose={handleDenyClose}
                    onConfirm={handleDenyConfirm}
                    request={requestToDeny}
                />
            ) : null}
        </>
    );
};
